<template>
  <div class="page-profile">
    <User :user="user" size="xl online-lg" :no-photo-display="true" />
    <div class="profile-info" :class="{ video }" v-if="user">
      <div class="about">
        <UserAbout :user="user" />
      </div>
      <div class="contact">
        <div class="block-title">{{ $t('pages.profile.profile.contact-title') }}</div>
        <a :href="phoneHref" v-if="user.phone && user.phone_visible">{{ user.phone }}</a>
        <a :href="emailHref" v-if="user.email_visible">{{ user.email }}</a>
      </div>
      <div class="social">
        <div class="block-title">{{ $t('pages.profile.edit.profile.social.title') }}</div>
        <div class="socials">
          <a :href="user.facebook_link" target="_blank" v-if="user.facebook_link">
            <img src="~@/assets/icons/logos/Facebook.svg" alt="facebook" />
          </a>
          <a :href="user.linkedin_link | link" target="_blank" v-if="user.linkedin_link">
            <img src="~@/assets/icons/logos/LinkedIn.svg" alt="LinkedIn" />
          </a>
          <a :href="user.instagram_link | link" target="_blank" v-if="user.instagram_link">
            <img src="~@/assets/icons/logos/Instagram.svg" alt="instagram" />
          </a>
        </div>
      </div>
      <VideoPlayer :src="user.video" />
    </div>
    <div class="actions" v-if="!userId">
      <btn-edit-profile />
      <button class="gray img-l" @click="exitAndDelete">
        <img src="@/assets/icons/delete-profile.svg" alt="" />
        <span>{{ $t('btn-delete-profile') }}</span>
      </button>
    </div>
    <div class="actions" v-else>
      <ChatOpenBtn :user="user" />
    </div>
    <div class="platform-info hide-pc">
      Platform for discussing environmental issues and developing international partnership
    </div>
  </div>
</template>

<script>
import User from '@/components/profile/User'
import BtnEditProfile from '@/components/buttons/btn-edit-profile'
import UserAbout from '@/components/user/UserAbout'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import BtnLogOut from '@/components/buttons/btn-log-out'
import ChatOpenBtn from '../../components/chat/ChatOpenBtn'
import VideoPlayer from '@/components/ui/VideoPlayer'

export default {
  name: 'Profile',
  components: { VideoPlayer, ChatOpenBtn, BtnLogOut, UserAbout, BtnEditProfile, User },
  created() {
    this.getUsersList().then(() => {
      if (!this.user) {
        this.getAttendees2020()
      }
    })
  },
  computed: {
    userId() {
      return this.$route.params.user
    },
    user() {
      if (this.userId) {
        return this.getUserId(+this.userId, true)
      } else return this.$auth.user()
    },
    phoneHref() {
      return `tel:${this.user.phone.replace(/[^0-9+]/g, '')}`
    },
    emailHref() {
      return `mailto:${this.user.email}`
    },
    video() {
      return this.$route.meta.video
    },
    ...mapState('users', ['users', 'usersReady']),
    ...mapGetters('users', ['getUserId']),
  },
  methods: {
    exitAndDelete() {
      this.deleteProfile()
      this.$auth.logout()
    },
    getUsersList() {
      if (this.userId && !this.usersReady) {
        return this.getUsers().then(() => {
          this.mfcProcessing()
        })
      } else {
        return Promise.resolve(true)
      }
    },
    ...mapActions('library', ['mfcProcessing']),
    ...mapActions('users', ['getUsers', 'getAttendees2020', 'deleteProfile']),
    ...mapMutations(['openModal']),
  },
  filters: {
    link(link) {
      if (!link || /^http/.test(link)) return link
      else return `//${link}`
    },
  },
}
</script>

<style lang="scss">
#content.page-profile {
  @include PC {
    padding: 18px 134px 54px 42px;
  }

  @include mobile {
    padding-left: 40px;
    padding-top: 30px;
  }

  .block-title {
    margin-bottom: 12px;
    font-weight: 700;
    font-size: 15px;
    line-height: 20px;
    color: #474747;
  }

  .user-block {
    margin-bottom: 82px;

    @include mobile {
      margin-bottom: 35px;
    }

    .avatar {
      @include flexCenter;
      color: transparent;
      font-weight: bold;
      font-size: 14px;
      line-height: 134%;
      user-select: none;

      &.empty {
        color: $dark;
      }

      @include PC {
        border-radius: 10px;
      }
    }
  }

  .user-video-block {
    margin-bottom: 50px;
  }

  .user-block .about {
    font-size: 14px;
    line-height: 21px;
    margin-top: 36px;

    @include mobile {
      font-weight: 400;
      font-size: 15px;
      line-height: 157.5%;
    }

    @include PC {
      margin-top: 0;
      &:not(.edit) {
        padding-left: 170px;
        padding-right: 18px;
      }
    }
  }

  .double-col {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 12px;
    margin-top: 37px;
  }

  .contact {
    a {
      display: block;
      font-weight: 400;
      font-size: 15px;
      line-height: 157.5%;
      color: #474747;
    }

    a + a {
      margin-top: 5px;
    }
  }

  .socials {
    display: flex;
    margin: 0 -2.5px;

    a {
      @include flexCenter;
      width: 34px;
      height: 34px;
      border-radius: 5px;
      background: rgba(204, 215, 230, 0.3);

      margin-right: 8px;

      &:last-child {
        margin-right: 0;
      }

      &,
      img {
        transition: all 0.25s;
      }

      &:hover {
        background: $primary;

        img {
          filter: brightness(100) grayscale(1);
        }
      }
    }
  }

  .actions {
    margin-top: 134px;
    display: flex;

    @include mobile {
      display: grid;
      grid-template-columns: 1fr auto;
      grid-gap: 10px;
      margin-top: 35px;

      > *:first-child:last-child {
        grid-column: 1 / 3;
      }
    }

    button {
      height: 41px;

      @include PC {
        padding: 12px 25px;
      }

      &:first-child:last-child {
        grid-column: 1 / 3;
      }
    }
  }

  @include mobile {
    .profile-info {
      display: flex;
      flex-direction: column;

      > div.video-player {
        order: -1;
        margin-top: 0;
        margin-bottom: 35px;
      }

      > *:not(:first-child) {
        margin-top: 35px;
      }
    }
  }

  .platform-info {
    margin-top: 60px;
    font-weight: 400;
    font-size: 14px;
    line-height: 135.3%;
    color: #ccd7e6;
  }

  @include PC {
    .profile-info {
      display: grid;
      grid-gap: 50px;
      grid-template-columns: 340px auto auto;
      margin-top: 53px;

      &.video {
        grid-gap: 64px 30px;
        grid-template-columns: 190px 166px 1fr;

        .about {
          grid-column: 1 / 3;
        }

        .video-player {
          grid-column: 3 / 4;
          grid-row: 1 / 3;
          margin-left: 26px;
          width: auto;
        }
      }

      &:not(.video) {
        > *:not(:last-child) {
          margin-bottom: 40px;
        }
      }

      .user-video-block {
        grid-row: 1 / span 2;
        grid-column: 2;
      }
    }
    > .actions {
      justify-items: flex-start;

      button {
        &:last-child:not(:first-child) {
          margin-left: 10px;
        }

        &:first-child:last-child {
          width: 170px;
        }
      }
    }
  }
}
</style>
